import { NaviTableColumnDef } from '@cp/src/components/NaviTable/interfaces';
import { LevelIcon } from '@cp/src/assets/icons/LevelIcon';
import { levelColorsList } from '@cp/src/components/AgentprogressMeter/constant';
import { formatAmount, formatPercent, shortNumberNotation } from '@cp/src/utils/commonUtils';
import MetricChangeCell from './MetricChangeCell';
import { Typography } from '@navi/web-ui/lib/primitives';
import styles from './index.module.scss';
import OverallMetricTooltip from './OverallMetricTooltip';
import { FormatType, IMetricData } from './interfaces';

export const getMetricValue = (value: any, metricFormat?: FormatType, shorthandNumber = true) => {
  if (value === null) {
    return '-';
  }
  switch (metricFormat) {
    case FormatType.PERCENT:
      return formatPercent(Math.abs(value));
    case FormatType.AMOUNT:
      if (shorthandNumber) {
        const amount = shortNumberNotation(value, 2);
        return `₹${amount ? amount : 0}`;
      }
      return formatAmount(value);
    case FormatType.NUMBER:
      return Math.round(value);
    default:
      return value;
  }
};

export const OVERALL_METRICS_COL_DEF: NaviTableColumnDef<IMetricData>[] = [
  {
    header: 'Agent name',
    accessorKey: 'agentName',
    size: 100,
    enableSorting: false,
    cell: info => {
      const { agentName, allocatedCases } = info.row.original || {};
      const { metricValueMTD } = allocatedCases || {};
      return agentName ? (
        <>
          <Typography variant="p3" color="var(--grayscale-2)" className={styles.agentName}>
            {agentName}
          </Typography>
          <Typography variant="p5" className="font-semibold truncate">
            {Math.round(metricValueMTD)} cases
          </Typography>
        </>
      ) : (
        <Typography variant="p3" className="font-semibold truncate">
          Overall
        </Typography>
      );
    },
    tooltip: true
  },
  {
    header: 'Level',
    accessorKey: 'performanceLevel',
    size: 60,
    headerTooltip: true,
    cell: info => {
      const { performanceLevel, agentName } = info.row.original || {};
      const { metricValueMTD } = performanceLevel || {};
      if (!agentName) {
        return null;
      }
      if (!metricValueMTD) return '-';
      return metricValueMTD < 10 ? (
        <LevelIcon level={metricValueMTD} color={levelColorsList[metricValueMTD - 1]} />
      ) : (
        '-'
      );
    }
  },
  {
    header: 'Cash collected per agent MTD',
    accessorKey: 'cashCollected',
    size: 120,
    headerTooltip: true,
    cell: info => {
      const { cashCollected } = info.row.original || {};
      const { metricValueMTD, changeInMetricValueFromLMSD, metricFormat } = cashCollected || {};
      return (
        <MetricChangeCell
          metric={{ value: metricValueMTD, change: changeInMetricValueFromLMSD, metricFormat }}
        />
      );
    },
    tooltip: info => {
      const { cashCollected } = info.row.original || {};
      const { metricValueMTD, metricValueLMSD, metricFormat } = cashCollected || {};
      return {
        content: (
          <OverallMetricTooltip
            data={{
              currentValue: metricValueMTD,
              lmsdValue: metricValueLMSD,
              metricFormat
            }}
          />
        ),
        hiddenPadding: 20
      };
    }
  },
  {
    header: 'EMI CE MTD (%)',
    accessorKey: 'emiCollectionEfficiency',
    headerTooltip: true,
    size: 90,
    cell: info => {
      const { emiCollectionEfficiency } = info.row.original || {};
      const { metricValueMTD, changeInMetricValueFromLMSD, metricFormat } =
        emiCollectionEfficiency || {};
      return (
        <MetricChangeCell
          metric={{
            value: metricValueMTD,
            change: changeInMetricValueFromLMSD,
            metricFormat
          }}
        />
      );
    },
    tooltip: info => {
      const { emiCollectionEfficiency } = info.row.original || {};
      const { metricValueMTD, metricValueLMSD, metricFormat } = emiCollectionEfficiency || {};
      return {
        content: (
          <OverallMetricTooltip
            data={{
              currentValue: metricValueMTD,
              lmsdValue: metricValueLMSD,
              metricFormat
            }}
          />
        ),
        hiddenPadding: 20
      };
    }
  },

  {
    header: 'EMI CE Target achieved (%)',
    accessorKey: 'emiCETargetAchievement',
    headerTooltip: true,
    size: 120,
    cell: info => {
      const { emiCETargetAchievement } = info.row.original || {};
      const { metricValueMTD, changeInMetricValueFromLMSD, metricFormat } =
        emiCETargetAchievement || {};
      return (
        <MetricChangeCell
          metric={{
            value: metricValueMTD,
            change: changeInMetricValueFromLMSD,
            metricFormat
          }}
        />
      );
    },
    tooltip: info => {
      const { emiCETargetAchievement } = info.row.original || {};
      const { metricValueMTD, metricValueLMSD, metricFormat } = emiCETargetAchievement || {};
      return {
        content: (
          <OverallMetricTooltip
            data={{
              currentValue: metricValueMTD,
              lmsdValue: metricValueLMSD,
              metricFormat
            }}
          />
        ),
        hiddenPadding: 20
      };
    }
  },
  {
    header: 'Coverage (%)',
    accessorKey: 'coverage',
    headerTooltip: true,
    size: 110,
    cell: info => {
      const { coverage } = info.row.original || {};
      const { metricValueMTD, changeInMetricValueFromLMSD, metricFormat } = coverage || {};
      return (
        <MetricChangeCell
          metric={{
            value: metricValueMTD,
            change: changeInMetricValueFromLMSD,
            metricFormat
          }}
        />
      );
    },
    tooltip: info => {
      const { coverage } = info.row.original || {};
      const { metricValueMTD, metricValueLMSD, metricFormat } = coverage || {};
      return {
        content: (
          <OverallMetricTooltip
            data={{
              currentValue: metricValueMTD,
              lmsdValue: metricValueLMSD,
              metricFormat
            }}
          />
        ),
        hiddenPadding: 20
      };
    }
  },
  {
    header: 'PTP (%)',
    accessorKey: 'ptp',
    headerTooltip: true,
    size: 100,
    cell: info => {
      const { ptp } = info.row.original || {};
      const { metricValueMTD, changeInMetricValueFromLMSD, metricFormat } = ptp || {};
      return (
        <MetricChangeCell
          metric={{
            value: metricValueMTD,
            change: changeInMetricValueFromLMSD,
            metricFormat
          }}
        />
      );
    },
    tooltip: info => {
      const { ptp } = info.row.original || {};
      const { metricValueMTD, metricValueLMSD, metricFormat } = ptp || {};
      return {
        content: (
          <OverallMetricTooltip
            data={{
              currentValue: metricValueMTD,
              lmsdValue: metricValueLMSD,
              metricFormat
            }}
          />
        ),
        hiddenPadding: 20
      };
    }
  },
  {
    header: 'PTP Converted (%)',
    accessorKey: 'ptpConversion',
    headerTooltip: true,
    size: 120,
    cell: info => {
      const { ptpConversion } = info.row.original || {};
      const { metricValueMTD, changeInMetricValueFromLMSD, metricFormat } = ptpConversion || {};
      return (
        <MetricChangeCell
          metric={{
            value: metricValueMTD,
            change: changeInMetricValueFromLMSD,
            metricFormat
          }}
        />
      );
    },
    tooltip: info => {
      const { ptpConversion } = info.row.original || {};
      const { metricValueMTD, metricValueLMSD, metricFormat } = ptpConversion || {};
      return {
        content: (
          <OverallMetricTooltip
            data={{
              currentValue: metricValueMTD,
              lmsdValue: metricValueLMSD,
              metricFormat
            }}
          />
        ),
        hiddenPadding: 20
      };
    }
  }
];

export const METRIC_PERFORMANCE_LEVEL = 'performanceLevel';

export enum AGENT_TYPES {
  LIVE_AGENTS = 'LIVE_AGENTS',
  TRAINING_AGENTS = 'TRAINING_AGENTS'
}

export const AGENT_TYPE_FILTER_OPTIONS = [
  {
    label: 'Live agents',
    value: AGENT_TYPES.LIVE_AGENTS
  },
  {
    label: 'Training agents',
    value: AGENT_TYPES.TRAINING_AGENTS
  }
];
